import { _debug } from '$extensionSrc/utils/logging'

import { StageDef } from '../automationMachineInterfaces'
import { REDDIT_AUTOMATION_STAGE_STATUSES } from './redditAutomationStageStatuses'
import * as redditUrls from './redditUrls'
import { getSwitchValue, toggleSwitch } from './switchUtils'
import { RedditSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { logError } from '../../utils/loggingUtils'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import {
  getLabelFromRadioEl,
  getRadioElByLabel,
  getRadioSelectedEl,
} from './radioUtils'

type ContentCommunityVisibilitySetting = {
  key: RedditSettingKey
  label: string
  oldLabel: string
  section: string
  url: string
  urlPattern?: string
  version: number
}

const settings: ContentCommunityVisibilitySetting[] = [
  {
    key: RedditSettingKey.CONTENT_VISIBILITY,
    version: 1,
    label: 'List your profile on old.reddit.com/users',
    oldLabel: 'Content visibility',
    section: 'settings-privacy-section',
    url: redditUrls.privacySettingsUrl,
  },
  {
    key: RedditSettingKey.CONTENT_VISIBILITY,
    version: 2,
    label: 'List your profile on old.reddit.com/users',
    oldLabel: 'Content visibility',
    section: 'settings-privacy-section',
    url: redditUrls.profileSettingsUrl,
    urlPattern: 'https:\\/\\/www\\.reddit\\.com\\/settings\\/profile(\\?.+)?',
  },
]

const NEW_COMMUNITY_VISIBILITY_VALUES = ['Show all', 'Customize', 'Hide all']

const getCurrentValueFn = (key: string) =>
  getCurrentValueFnFromKey(key, (value) => {
    if (
      key === RedditSettingKey.COMMUNITY_VISIBILITY &&
      NEW_COMMUNITY_VISIBILITY_VALUES.includes(value)
    ) {
      return value
    }
    return value === true ? 'On' : 'Off'
  })

const getIsFulfilledFn = (key: string) =>
  getIsFulfilledFnFromKey(key, (value) => {
    if (
      key === RedditSettingKey.COMMUNITY_VISIBILITY &&
      NEW_COMMUNITY_VISIBILITY_VALUES.includes(value)
    ) {
      return value !== 'Show all'
    }
    return value === false
  })

const gatherStageDefs: StageDef[] = [
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `GATHER_REDDIT_SETTING_${s.key}_${s.version}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: s.url,
    urlPattern: s.urlPattern || undefined,
    action: async (_machineContext, userContext) => {
      try {
        const value = await getSwitchValue(s.section, s.label, s.oldLabel)
        await userContext.updateStoredInitialValues(
          {
            [s.key]: value,
          },
          enums.RedditRecommendationKey.REDDIT_CONTENT_COMMUNITY_VISIBILITY,
        )
      } catch (error) {
        // swallow error as they're expected for users with new settings pages
        // the next stage will try with the updated url
        logError(error, 'redditContentCommunityVisibility')
      }
    },
  })),
  {
    type: enums.StageType.ACTION,
    key: `GATHER_REDDIT_SETTING_COMMUNITY_VISIBILITY`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: redditUrls.profileSettingsUrl,
    urlPattern: 'https:\\/\\/www\\.reddit\\.com\\/settings\\/profile(\\?.+)?',
    action: async (_machineContext, userContext) => {
      try {
        const value = await Promise.any([
          getSwitchValue(
            'settings-profile-section',
            'Show active communities',
            'Active in communities visibility',
          ),
          (async () => {
            const selectedElement = await getRadioSelectedEl(
              [
                'settings-profile-visibility-section',
                'settings-profile-content-and-activity-section',
              ],
              'content-and-activity-option',
            )

            if (!selectedElement) {
              throw new Error('Element not found')
            }

            return getLabelFromRadioEl(selectedElement)
          })(),
        ])

        await userContext.updateStoredInitialValues(
          {
            [RedditSettingKey.COMMUNITY_VISIBILITY]: value,
          },
          enums.RedditRecommendationKey.REDDIT_CONTENT_COMMUNITY_VISIBILITY,
        )
      } catch (error) {
        // swallow error as they're expected for users with new settings pages
        // the next stage will try with the updated url
        logError(error, 'redditContentCommunityVisibility')
      }
    },
  },
]

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UPDATE_SETTING_${s.key}_${s.version}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: s.url,
    urlPattern: s.urlPattern || undefined,
    action: async (machineContext, userContext) => {
      if (
        !userContext.settingsSelected ||
        !userContext.settingsSelected[s.key]
      ) {
        return
      }

      try {
        const currentValue = await getSwitchValue(
          s.section,
          s.label,
          s.oldLabel,
        )
        if (!userContext.isDry && currentValue !== false) {
          await toggleSwitch(s.section, s.label, s.oldLabel)
        }
      } catch (error) {
        // swallow error as they're expected for users with new settings pages
        // the next stage will try with the updated url
        logError(error, 'redditContentCommunityVisibility')
      }
    },
  })),
  {
    type: enums.StageType.ACTION,
    key: `UPDATE_SETTING_COMMUNITY_VISIBILITY_2`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: redditUrls.profileSettingsUrl,
    urlPattern: 'https:\\/\\/www\\.reddit\\.com\\/settings\\/profile(\\?.+)?',
    action: async (_machineContext, userContext) => {
      if (
        !userContext.settingsSelected ||
        !userContext.settingsSelected[RedditSettingKey.COMMUNITY_VISIBILITY]
      ) {
        return
      }

      try {
        await Promise.any([
          (async () => {
            const currentValue = await getSwitchValue(
              'settings-profile-section',
              'Show active communities',
              'Active in communities visibility',
            )
            if (!userContext.isDry && currentValue !== false) {
              await toggleSwitch(
                'settings-profile-section',
                'Show active communities',
                'Active in communities visibility',
              )
            }
          })(),
          (async () => {
            const selectedElement = await getRadioSelectedEl(
              [
                'settings-profile-visibility-section',
                'settings-profile-content-and-activity-section',
              ],
              'content-and-activity-option',
            )

            let selectedLabel
            if (selectedElement) {
              selectedLabel = getLabelFromRadioEl(selectedElement)
            }

            if (userContext.isDry || selectedLabel !== 'Show all') {
              _debug(
                {
                  isDry: userContext.isDry,
                  selectedLabel,
                  msg: 'aborting update',
                },
                'redditContentCommunityVisibility',
                'd',
              )
              return
            }

            const el = await getRadioElByLabel(
              [
                'settings-profile-visibility-section',
                'settings-profile-content-and-activity-section',
              ],
              'content-and-activity-option',
              'Hide all',
            )

            if (!el) {
              throw new Error("Could not find element for 'Hide all'")
            }

            el.click()
          })(),
        ])
      } catch (error) {
        // swallow error as they're expected for users with new settings pages
        // the next stage will try with the updated url
        logError(error, 'redditContentCommunityVisibility')
      }
    },
  },
]

const undoStageDefs: StageDef[] = [
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UNDO_SETTING_${s.key}_${s.version}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: s.url,
    urlPattern: s.urlPattern !== undefined ? s.urlPattern : undefined,
    action: async (machineContext, userContext) => {
      if (!userContext.initialValues || !userContext.initialValues[s.key]) {
        return
      }

      try {
        const currentValue = await getSwitchValue(
          s.section,
          s.label,
          s.oldLabel,
        )
        if (
          !userContext.isDry &&
          currentValue !== userContext.initialValues[s.key]
        ) {
          await toggleSwitch(s.section, s.label, s.oldLabel)
        }
      } catch (error) {
        // swallow error as they're expected for users with new settings pages
        // the next stage will try with the updated url
        logError(error, 'redditContentCommunityVisibility')
      }
    },
  })),
  {
    type: enums.StageType.ACTION,
    key: `UNDO_SETTING_COMMUNITY_VISIBILITY_2`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: redditUrls.profileSettingsUrl,
    urlPattern: 'https:\\/\\/www\\.reddit\\.com\\/settings\\/profile(\\?.+)?',
    action: async (_machineContext, userContext) => {
      if (
        !userContext.initialValues ||
        !userContext.initialValues[RedditSettingKey.COMMUNITY_VISIBILITY] ||
        userContext.initialValues[RedditSettingKey.COMMUNITY_VISIBILITY] !==
          'Show all'
      ) {
        return
      }

      try {
        const initialValue =
          userContext.initialValues[RedditSettingKey.COMMUNITY_VISIBILITY]
        await Promise.any([
          (async () => {
            const currentValue = await getSwitchValue(
              'settings-profile-section',
              'Show active communities',
              'Active in communities visibility',
            )
            if (
              !userContext.isDry &&
              currentValue !==
                userContext.initialValues[RedditSettingKey.COMMUNITY_VISIBILITY]
            ) {
              await toggleSwitch(
                'settings-profile-section',
                'Show active communities',
                'Active in communities visibility',
              )
            }
          })(),
          (async () => {
            if (!userContext.isDry) {
              const el = await getRadioElByLabel(
                [
                  'settings-profile-visibility-section',
                  'settings-profile-content-and-activity-section',
                ],
                'content-and-activity-option',
                initialValue === false ? 'Hide all' : initialValue,
              )
              el.click()
            }
          })(),
        ])
      } catch (error) {
        // swallow error as they're expected for users with new settings pages
        // the next stage will try with the updated url
        logError(error, 'redditContentCommunityVisibility')
      }
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
